import { HireCarPassengers, IOptionsWithTypes, SectionId } from "@eventr/common";
import { ColoursObj, Icon } from "@eventr/common-storybook";
import { Box, Stack } from "@mui/material";
import { cardTranslation } from "i18n/translateHelpers";
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { routes } from "routing";
import useDerivedScreenType from "v2/hooks/useDerivedScreenType";
import { useFormatValue } from "../../hooks/useFormatValue";
import { useMatchTagsAndUsers } from "../hooks/useMatchTagsAndUsers";
import { useObjectContext } from "../providers/objectProvider";
import { CardText } from "../subComponents/cardText";
import { CategoryList } from "../subComponents/categoryList";
import { ChatButton } from "../subComponents/chatButton";
import { EditButton } from "../subComponents/editButton";
import { ExpandableSection } from "../subComponents/expendableSection";
import { RowInfo } from "../subComponents/rowInfo";
import { RowInfoNotes } from "../subComponents/rowInfoNotes";
import { HireCarAttachments } from "./hireCarAttachment";
import { HireCarDriver } from "./hireCarDriver";
import { HireCarPassenger } from "./hireCarPassenger";

interface IHireCarInfoProps {
  pick_up: string;
  drop_off: string;
  booking_ref?: string;
  pick_up_location?: string;
  drop_off_location?: string;
  documents?: string;
  additional_info?: string;
  passengers?: HireCarPassengers[];
  vehicle_type?: string;
  provider?: string;
  chatNames: string[];
  variant?: "expandable" | "static";
  categories?: string[];
  /** Leaves the guest's chat to the page, which carries it elsewhere. */
  joinOnly?: boolean;
}

export const HireCarInfo = ({
  booking_ref,
  pick_up,
  pick_up_location,
  drop_off,
  drop_off_location,
  documents,
  additional_info,
  passengers,
  vehicle_type,
  provider,
  chatNames,
  variant = "expandable",
  categories,
  joinOnly,
}: IHireCarInfoProps) => {
  const screenType = useDerivedScreenType();
  const { _id, sectionID, past } = useObjectContext();
  const { itemId: idFromParams = "" } = useParams();
  const { formatValue } = useFormatValue();
  const { matchTagsAndUser } = useMatchTagsAndUsers();

  const allGuests = useMemo(() => {
    const all: IOptionsWithTypes[] = [];
    passengers?.forEach((passenger) => {
      all.push(...(passenger.users ?? []));
    });
    return all;
  }, [passengers]);

  const end = useMemo(() => {
    if (screenType === "edit") {
      return <EditButton path={routes.edit.sectionItem.build({ sectionId: sectionID, itemId: _id || idFromParams })} />;
    }

    return (
      <ChatButton
        _id={_id || idFromParams}
        users={allGuests}
        names={chatNames}
        sectionId={SectionId.HIRE_CAR}
        joinOnly={joinOnly}
      />
    );
  }, [screenType, _id, allGuests, chatNames, sectionID, idFromParams, joinOnly]);

  const driverList = useMemo(() => {
    if ((passengers?.length ?? 0) === 0) return null;
    return (passengers ?? []).filter((p) => p.isDriver).map((d) => d.users);
  }, [passengers]);

  const passengerList = useMemo(() => {
    if ((passengers?.length ?? 0) === 0) return null;
    return (passengers ?? []).filter((p) => !p.isDriver).map((p) => p.users);
  }, [passengers]);

  const passenger = useMemo(() => {
    if ((passengers?.length ?? 0) === 0) return null;

    return (passengers ?? []).filter((p) => p?.users?.some((user) => matchTagsAndUser(user)));
  }, [passengers, matchTagsAndUser]);

  const cardText = (
    <CardText variant="body1">{booking_ref ? booking_ref : cardTranslation("Additional Information")}</CardText>
  );
  const content = (
    <Stack spacing="8px">
      <CardText variant="bodyBold">{cardTranslation("Pick-up & Drop-off")}</CardText>

      {pick_up && (
        <Stack>
          <RowInfo
            start={
              <Icon name="Right" bubble bubbleColour={past ? ColoursObj.shades.shade2 : ColoursObj.core.secondary} />
            }
            middle={<CardText>{cardTranslation("Pick-up")}</CardText>}
            end={<CardText>{formatValue(pick_up, "datetime")}</CardText>}
          />
          {pick_up_location && <CardText variant="bodySubtext">{pick_up_location}</CardText>}
        </Stack>
      )}

      {drop_off && (
        <Stack>
          <RowInfo
            start={
              <Icon name="Left" bubble bubbleColour={past ? ColoursObj.shades.shade2 : ColoursObj.core.secondary} />
            }
            middle={<CardText>{cardTranslation("Drop-off")}</CardText>}
            end={<CardText>{formatValue(drop_off, "datetime")}</CardText>}
          />
          {drop_off_location && <CardText variant="bodySubtext">{drop_off_location}</CardText>}
        </Stack>
      )}

      {(vehicle_type || provider) && <CardText variant="bodyBold">{cardTranslation("Car Information")}</CardText>}

      {vehicle_type && (
        <RowInfo
          start={
            <Icon
              name="Car_Type"
              bubble
              bubbleColour={past ? ColoursObj.shades.shade2 : ColoursObj.coreBrights.secondary}
            />
          }
          middle={<CardText variant="body1">{cardTranslation("Vehicle Type")}</CardText>}
          end={<CardText variant="body1">{vehicle_type}</CardText>}
        />
      )}

      {provider && (
        <RowInfo
          start={
            <Icon name="Group" bubble bubbleColour={past ? ColoursObj.shades.shade2 : ColoursObj.coreBrights.primary} />
          }
          middle={<CardText variant="body1">{cardTranslation("Provider")}</CardText>}
          end={<CardText variant="body1">{provider}</CardText>}
        />
      )}

      {(documents || additional_info) && <CardText variant="bodyBold">{cardTranslation("Attachments")}</CardText>}

      {additional_info && <RowInfoNotes notes={additional_info} />}
      {documents && <HireCarAttachments extra_docs={documents} />}

      {(screenType === "personal" || screenType === "upNext") &&
        passenger &&
        passenger.some((p) => p.notes || p.documents) && (
          <CardText variant="bodyBold">{cardTranslation("Personal Information")}</CardText>
        )}

      {(screenType === "personal" || screenType === "upNext") &&
        passenger &&
        passenger.map((p, i) => p.notes && <RowInfoNotes key={i} notes={p.notes} />)}

      {(screenType === "personal" || screenType === "upNext") &&
        passenger &&
        passenger.map((p, i) => p.documents && <HireCarAttachments key={i} extra_docs={p.documents} />)}
      <CategoryList categories={categories} />

      {driverList && driverList.length > 0 && <HireCarDriver driver={driverList.flat()} />}

      {passengerList && <HireCarPassenger passenger={passengerList.flat()} />}
    </Stack>
  );

  if (variant === "static") {
    return (
      <Box x-sp-c="ListKeyPoint">
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {cardText}
          {end}
        </Stack>
        {content}
      </Box>
    );
  }

  return (
    <ExpandableSection mid={cardText} end={end}>
      {content}
    </ExpandableSection>
  );
};
